import React from "react";
import { SearchQueryFunnelResponse } from "../../api/analyticsAdmin";
import { SearchSuggestionViewModel } from "../../api/generated/models/SearchSuggestionViewModel";
import { formatInteger } from "../../helpers/formatNumber";
import { queryLabel } from "../../helpers/searchAnalytics";
import { describeMapping, normalizeKeyword } from "../../helpers/searchSuggestions";
import SectionBody from "./SectionBody";
import { SectionState } from "./useAnalyticsStream";

const MIN_SEARCHES_HINT =
  "Queries searched fewer times than this are hidden to reduce noise.";

const ZeroResultQueries: React.FC<{
  section: SectionState<SearchQueryFunnelResponse>;
  minSearches: number;
  onMinSearchesChange: (minSearches: number) => void;
  suggestionsByKeyword: Map<string, SearchSuggestionViewModel>;
  onMap: (query: string) => void;
  onEdit: (suggestion: SearchSuggestionViewModel) => void;
}> = ({
  section,
  minSearches,
  onMinSearchesChange,
  suggestionsByKeyword,
  onMap,
  onEdit,
}) => (
  <section className="mb-8">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
      <h3 className="text-lg font-medium">Zero-result queries</h3>
      <label className="text-sm text-gray-600" title={MIN_SEARCHES_HINT}>
        Min searches{" "}
        <input
          type="number"
          min={0}
          value={minSearches}
          onChange={(e) =>
            onMinSearchesChange(Math.max(0, Number(e.target.value) || 0))
          }
          title={MIN_SEARCHES_HINT}
          className="w-20 p-2 border border-gray-300 rounded-lg text-sm ml-1"
        />
      </label>
    </div>
    <p className="text-xs text-gray-400 mb-4">
      Queries that always return zero results, highest-volume first. Map merchants
      to a query and the web app shows them under "you might be interested in"
      instead of an empty page.
    </p>

    <SectionBody
      section={section}
      isEmpty={(data) => data.items.length === 0}
      emptyMessage="No always-empty queries above the min-searches threshold. 🎉"
    >
      {(data) => (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">Query</th>
                <th className="text-right font-medium px-4 py-3">Searches</th>
                <th className="text-right font-medium px-4 py-3">Searchers</th>
                <th className="text-right font-medium px-4 py-3">Suggestion</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => {
                const mapped = suggestionsByKeyword.get(normalizeKeyword(row.query));
                return (
                  <tr key={row.query} className="border-t border-gray-100">
                    <td className="px-4 py-3">{queryLabel(row)}</td>
                    <td className="px-4 py-3 text-right">
                      {formatInteger(row.searches)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatInteger(row.uniqueSearchers)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {mapped ? (
                        <button
                          type="button"
                          onClick={() => onEdit(mapped)}
                          className={`text-xs px-2 py-1 rounded-full ${
                            mapped.isPublished
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {mapped.isPublished ? "Mapped" : "Mapped · draft"} ·{" "}
                          {describeMapping(mapped)}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onMap(queryLabel(row))}
                          className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20"
                        >
                          Map merchants
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionBody>
  </section>
);

export default ZeroResultQueries;
